import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en/common.json';
import { DEFAULT_LANGUAGE, LANGUAGES, isLanguageCode, type LanguageCode } from './languages';

export { LANGUAGES, isLanguageCode, type LanguageCode } from './languages';

/** English is bundled (fallback); other languages load on demand. */
export async function initI18n(lang: LanguageCode = DEFAULT_LANGUAGE): Promise<void> {
  await i18next.use(initReactI18next).init({
    resources: { en: { common: en } },
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  if (lang !== 'en') await setLanguage(lang);
  else document.documentElement.lang = 'en';
}

export async function setLanguage(lang: LanguageCode): Promise<void> {
  if (!isLanguageCode(lang)) return;
  if (!i18next.hasResourceBundle(lang, 'common')) {
    const entry = LANGUAGES.find((l) => l.code === lang);
    if (entry) {
      const mod = await entry.load();
      i18next.addResourceBundle(lang, 'common', mod.default, true, true);
    }
  }
  await i18next.changeLanguage(lang);
  document.documentElement.lang = lang;
}

/** Loads every language's strings without changing the language in use. */
export async function loadAllLanguages(): Promise<void> {
  await Promise.all(
    LANGUAGES.map(async ({ code, load }) => {
      if (i18next.hasResourceBundle(code, 'common')) return;
      const mod = await load();
      i18next.addResourceBundle(code, 'common', mod.default, true, true);
    }),
  );
}

/** A string in the language in use, then in the other loaded languages. */
export function translationsOf(key: string): string[] {
  const others = LANGUAGES.filter(({ code }) => i18next.hasResourceBundle(code, 'common')).map(
    ({ code }) => i18next.getFixedT(code)(key) as string,
  );
  return [...new Set([i18next.t(key) as string, ...others])];
}

/** Translate outside React (command bus, platform adapters). */
export const t = (key: string, options?: Record<string, unknown>): string =>
  i18next.t(key, options) as string;
